<template>
  <validation-observer ref="form" novalidate>
    <div class="signature-tab">
      <div v-if="!hasCompletedSignature" class="signature-warning">
        <i class="fas fa-bell-exclamation mr-2" />
        Es requerido completar el proceso de firma para el Referido a
        Rehabilitación Vocacional (ARV).
      </div>

      <div v-if="hasValidationItems" class="tab-validation-warning mt-3">
        <div class="tab-validation-warning__message">
          <i class="fas fa-bell-exclamation mr-2" />
          Complete los pendientes de firma.
        </div>
        <ul class="tab-validation-warning__items">
          <li v-for="item in validationItems" :key="item.id">
            {{ item.label }}
          </li>
        </ul>
      </div>

      <sign-plan
        ref="signPlanRef"
        class="my-4"
        :disabled="isReadonlyMode"
        :is-electronic-signature="selectedMechanismSignPlan"
        question-label="¿Cuál será el mecanismo que se estará utilizando para firmar Referido a Rehabilitación Vocacional (ARV)?"
        @change="handleSelectMechanism"
      />

      <div v-if="isElectronic" class="signature-panel">
        <div v-if="loadingSignatureHub" class="signature-placeholder">
          Cargando configuración de firma electrónica...
        </div>
        <signature-hub-info
          v-else
          :signature-info="signatureHubInfo"
          @configure-sign="handleConfigureSign"
        />
      </div>

      <div v-if="isManual" class="signature-panel">
        <div v-if="showManualForm" class="manual-form">
          <validation-provider
            v-slot="{ errors }"
            name="Representante escolar"
            :rules="nameRules(true)"
          >
            <div>
              <label>Representante escolar:</label>
              <input
                v-model.trim="model.evaluatorName"
                class="form-control"
                :disabled="isReadonlyMode"
              />
              <error
                v-if="errors[0]"
                variant="no-border"
                :message="errors[0]"
              />
            </div>
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
            name="Inicial"
            :rules="initialRules"
          >
            <div>
              <label>Inicial:</label>
              <input
                v-model.trim="model.evaluatorInitialName"
                class="form-control"
                maxlength="1"
                :disabled="isReadonlyMode"
              />
              <error
                v-if="errors[0]"
                variant="no-border"
                :message="errors[0]"
              />
            </div>
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
            name="Apellido Paterno"
            :rules="nameRules(true)"
          >
            <div>
              <label>Apellido Paterno:</label>
              <input
                v-model.trim="model.evaluatorLastName"
                class="form-control"
                :disabled="isReadonlyMode"
              />
              <error
                v-if="errors[0]"
                variant="no-border"
                :message="errors[0]"
              />
            </div>
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
            name="Apellido Materno"
            :rules="nameRules(false)"
          >
            <div>
              <label>Apellido Materno:</label>
              <input
                v-model.trim="model.evaluatorSecondLastName"
                class="form-control"
                :disabled="isReadonlyMode"
              />
              <error
                v-if="errors[0]"
                variant="no-border"
                :message="errors[0]"
              />
            </div>
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
            name="Correo"
            rules="required"
          >
            <div>
              <label>Correo:</label>
              <input
                v-model.trim="model.evaluatorEmail"
                class="form-control"
                :disabled="isReadonlyMode"
                :error="errors[0]"
                :custom-error-msg="'El campo Correo es requerido.'"
              />
              <error
                v-if="errors[0]"
                variant="no-border"
                :message="errors[0]"
              />
            </div>
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
            class="manual-form__date-field"
            name="Fecha"
            rules="required"
          >
            <custom-date-picker
              v-model="model.evaluatorDate"
              class-container=""
              :disabled="isReadonlyMode"
              :error="errors[0]"
              format="dd/MMM./yyyy"
              label="Fecha:"
              :min-date="new Date()"
              placeholder="DD/MM/YYYY"
              :custom-error-msg="'El campo Fecha es requerido.'"
            />
          </validation-provider>

          <div class="manual-form__actions">
            <action-button
              i="fas fa-file-medical"
              text="Añadir"
              variant="success"
              :disabled="isReadonlyMode"
              @click="addManualSigner"
            />
          </div>
        </div>

        <template v-else>
          <div class="manual-summary">
            <div>
              <span>Representante escolar:</span>
              <strong>{{ model.evaluatorName }}</strong>
            </div>
            <div>
              <span>Inicial:</span>
              <strong>{{ model.evaluatorInitialName || '-' }}</strong>
            </div>
            <div>
              <span>Apellido Paterno:</span>
              <strong>{{ model.evaluatorLastName }}</strong>
            </div>
            <div>
              <span>Apellido Materno:</span>
              <strong>{{ model.evaluatorSecondLastName || '-' }}</strong>
            </div>
            <div>
              <span>Correo:</span>
              <strong>{{ model.evaluatorEmail || '-' }}</strong>
            </div>
            <div>
              <span>Fecha:</span>
              <strong>{{ model.evaluatorDate | date }}</strong>
            </div>
            <button
              v-if="!isReadonlyMode"
              class="manual-summary__clear"
              type="button"
              @click="clearManualSigner"
            >
              <i class="fas fa-times-circle" />
            </button>
          </div>

          <section-title class="my-3" content="Documento" />

          <div
            v-if="showDownloadTemplate"
            class="document-row document-row--center"
          >
            <action-button
              i="fas fa-file-download"
              text="Descargar Referido a Rehabilitación Vocacional"
              variant="outline-success"
              @click="downloadTemplate"
            />
          </div>

          <div v-else class="document-row">
            <div>
              <span>Documento:</span>
              <strong>Referido a Rehabilitación Vocacional</strong>
            </div>
            <div>
              <span>Archivo:</span>
              <strong v-if="hasSignedDocument">
                {{ model.signedDocumentName }}
              </strong>
              <validation-provider v-else v-slot="{ errors }" rules="required">
                <custom-file-input
                  v-model="selectedSignedDocument"
                  :accept="'.pdf'"
                  custom-error-msg="Favor seleccionar un documento PDF."
                  display-style="normal"
                  :error="errors[0]"
                  label-position="left"
                  :on-download="noop"
                  @input="saveSignedDocumentFile"
                />
              </validation-provider>
            </div>
            <div v-if="hasSignedDocument" class="document-row__actions">
              <action-button
                v-if="canDeleteSignedDocument"
                class="mr-2"
                i="fas fa-times-circle"
                variant="outline-danger"
                @click="clearSignedDocument"
              />
              <action-button
                i="fas fa-file-download"
                variant="success"
                @click="downloadSignedDocument"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </validation-observer>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import alertMixin from '@/mixin/alert.mixin.js';
import objectToFormData from 'object-to-formdata';

import ActionButton from 'ActionButton';
import CustomDatePicker from 'custom-date-picker';
import CustomFileInput from 'CustomFileInput';
import Error from '@/components/common/Error';
import SectionTitle from 'SectionTitle';
import { SignPlan } from '@/components/sign-plan';
import { SignatureHubInfo } from '@quisit/signature-hub';

import documentsService from '@/services/documents';
import peiGeneralService from '@/services/pei/general';
import {
  getReport,
  save,
  saveSignedDocument,
} from '@/services/vocational-rehabilitation-referral/api.js';
import {
  getSignatureHubInfo,
  removeSignatureHub,
} from '@/services/signatureHub/api';
import { date } from '@/utils/filters';
import { copy } from '@/utils/objUtil';
import digitalSignatureDocumentStatus from '@/utils/constants/digitalSignatureDocumentStatus';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes';
import Statuses from '@/utils/constants/vocational-rehabilitation-referral-statuses';
import { SignatureMechanisms } from './model';

const NAME_REGEX = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü ]+$/;

const getFirstAvailableValue = (source, fields) =>
  fields.map((field) => (source ? source[field] : null)).find(Boolean) || '';

export default {
  name: 'SignatureTab',
  components: {
    ActionButton,
    CustomDatePicker,
    CustomFileInput,
    Error,
    SectionTitle,
    SignatureHubInfo,
    SignPlan,
    ValidationObserver,
    ValidationProvider,
  },
  filters: {
    date,
  },
  mixins: [alertMixin],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
    value: {
      type: Object,
      required: true,
    },
    readOnly: Boolean,
    isReadOnly: Boolean,
    status: {
      type: Object,
      default: () => null,
    },
    validationItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      hasDownloadedTemplate: false,
      manualSignerAdded: false,
      manualSignatureCleared: false,
      manualProfilePrefilled: false,
      selectedSignedDocument: null,
      selectedMechanismSignPlan: null,
      signatureHubInfo: null,
      loadingSignatureHub: false,
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isReadonlyMode() {
      return this.readOnly || this.isReadOnly;
    },
    isDraft() {
      return Number(this.status?.id) === Statuses.Draft;
    },
    canDeleteSignedDocument() {
      return this.isCompleted && !this.isReadonlyMode;
    },
    isCompleted() {
      return Number(this.status?.id) === Statuses.Completed;
    },
    isElectronicSignature: {
      get() {
        if (
          Number(this.model.signatureMechanismId) ===
          SignatureMechanisms.Electronic
        ) {
          return true;
        }

        if (
          Number(this.model.signatureMechanismId) === SignatureMechanisms.Manual
        ) {
          return false;
        }

        return null;
      },
      set(value) {
        this.model = {
          ...this.model,
          signatureMechanismId:
            value === true
              ? SignatureMechanisms.Electronic
              : value === false
              ? SignatureMechanisms.Manual
              : null,
        };
      },
    },
    isManual() {
      return (
        Number(this.model.signatureMechanismId) === SignatureMechanisms.Manual
      );
    },
    isElectronic() {
      return (
        Number(this.model.signatureMechanismId) ===
        SignatureMechanisms.Electronic
      );
    },
    hasManualSignerInfo() {
      return [
        this.model.evaluatorName,
        this.model.evaluatorLastName,
        this.model.evaluatorDate,
      ].every(Boolean);
    },
    hasSignedDocument() {
      return !!this.model.signedDocumentId;
    },
    isElectronicSigned() {
      return (
        this.signatureHubInfo?.documentInfo?.documentStatusId ===
        digitalSignatureDocumentStatus.Signed
      );
    },
    hasCompletedSignature() {
      if (this.isElectronic) return this.isElectronicSigned;
      if (this.isManual) return this.hasSignedDocument;

      return false;
    },
    hasValidationItems() {
      return this.validationItems.length > 0;
    },
    initialRules() {
      return {
        max: 1,
        regex: NAME_REGEX,
      };
    },
    showManualForm() {
      return (
        !this.manualSignerAdded &&
        (!this.hasManualSignerInfo || this.manualProfilePrefilled)
      );
    },
    showDownloadTemplate() {
      return !this.hasDownloadedTemplate && !this.hasSignedDocument;
    },
    initialModel() {
      return copy(this.model);
    },
  },
  watch: {
    hasManualSignerInfo: {
      immediate: true,
      handler(value) {
        this.manualSignerAdded =
          value && !this.manualSignatureCleared && !this.manualProfilePrefilled;
      },
    },
    isManual: {
      immediate: true,
      handler(value) {
        if (value) this.prefillManualSignerFromProfile();
      },
    },
    isElectronic: {
      immediate: true,
      handler(value) {
        if (value) {
          this.fetchSignatureHubInfo();
          return;
        }

        this.signatureHubInfo = null;
      },
    },
    'value.signatureMechanismId': {
      immediate: true,
      handler(value) {
        const selected = this.toSignPlanValue(value);
        if (this.selectedMechanismSignPlan === selected) return;

        this.selectedMechanismSignPlan = selected;
        this.$nextTick(() =>
          this.$refs.signPlanRef?.setSelectedQuestion(selected)
        );
      },
    },
  },
  mounted() {
    this.selectedMechanismSignPlan = this.toSignPlanValue(
      this.model.signatureMechanismId
    );
    if (this.isManual) this.prefillManualSignerFromProfile();
  },
  activated() {
    this.clearStaleManualSignature();
  },
  methods: {
    nameRules(required) {
      return {
        required,
        min: 2,
        max: 50,
        regex: NAME_REGEX,
      };
    },
    noop() {},
    getLoggedUserSignerInfo() {
      const profile = this.userProfile || {};
      const fullName = getFirstAvailableValue(profile, ['fullName', 'name']);
      const nameParts = fullName.split(' ').filter(Boolean);

      return {
        evaluatorName:
          getFirstAvailableValue(profile, [
            'firstName',
            'firstname',
            'givenName',
            'givenname',
          ]) ||
          nameParts[0] ||
          '',
        evaluatorInitialName: getFirstAvailableValue(profile, [
          'initial',
          'middleInitial',
        ]),
        evaluatorLastName:
          getFirstAvailableValue(profile, [
            'lastName',
            'lastname',
            'lastPaternalName',
            'lastpaternalname',
          ]) || (nameParts.length > 1 ? nameParts[nameParts.length - 2] : ''),
        evaluatorSecondLastName:
          getFirstAvailableValue(profile, [
            'secondLastName',
            'secondlastname',
            'lastMaternalName',
            'lastmaternalname',
          ]) || (nameParts.length > 2 ? nameParts[nameParts.length - 1] : ''),
        evaluatorEmail: getFirstAvailableValue(profile, [
          'email',
          'emailAddress',
          'emailaddress',
        ]),
        evaluatorDate: new Date(),
      };
    },
    prefillManualSignerFromProfile() {
      if (this.hasManualSignerInfo || this.manualSignatureCleared) return;

      const signerInfo = this.getLoggedUserSignerInfo();
      this.model = {
        ...this.model,
        evaluatorName: this.model.evaluatorName || signerInfo.evaluatorName,
        evaluatorInitialName:
          this.model.evaluatorInitialName || signerInfo.evaluatorInitialName,
        evaluatorLastName:
          this.model.evaluatorLastName || signerInfo.evaluatorLastName,
        evaluatorSecondLastName:
          this.model.evaluatorSecondLastName ||
          signerInfo.evaluatorSecondLastName,
        evaluatorEmail: this.model.evaluatorEmail || signerInfo.evaluatorEmail,
        evaluatorDate: this.model.evaluatorDate || signerInfo.evaluatorDate,
      };
      this.manualProfilePrefilled = true;
      this.manualSignerAdded = false;
    },
    async fetchSignatureHubInfo() {
      if (!this.id || Number(this.id) <= 0) return;

      try {
        this.loadingSignatureHub = true;
        const { data } = await getSignatureHubInfo(
          SignatureHubTypes.REFERRAL_VOCATIONAL_REHABILITATION,
          this.id
        );
        this.signatureHubInfo = data || null;
        if (this.isElectronicSigned) this.$emit('clear-validation');
      } catch {
        this.signatureHubInfo = null;
      } finally {
        this.loadingSignatureHub = false;
      }
    },
    async handleConfigureSign() {
      await this.saveSignatureInfo(SignatureMechanisms.Electronic);

      this.$router.push({
        name: 'SignatureHub',
        params: {
          typeId: SignatureHubTypes.REFERRAL_VOCATIONAL_REHABILITATION,
          relatedId: this.id,
          from: 'vocationalRehabilitationReferral',
        },
      });
    },
    async validate() {
      const signPlanValid = await this.$refs.signPlanRef?.validate();
      const formValid = await this.$refs.form?.validate();

      if (
        Number(this.model.signatureMechanismId) ===
        SignatureMechanisms.Electronic
      ) {
        await this.fetchSignatureHubInfo();
      }

      const items = this.getPendingItems();

      return {
        tabId: 'Signature',
        title: 'Firma',
        valid: Boolean(signPlanValid) && Boolean(formValid) && !items.length,
        items,
      };
    },
    getPendingItems() {
      const items = [];
      const add = (id, label, selector = '.signature-tab') =>
        items.push({ id, label, selector });

      if (!this.model.signatureMechanismId) {
        add('signatureMechanismId', 'Seleccione el mecanismo de firma.');
        return items;
      }

      if (this.isManual) {
        if (!this.model.evaluatorName) {
          add('evaluatorName', 'Representante escolar.', '.manual-form');
        }

        if (!this.model.evaluatorLastName) {
          add('evaluatorLastName', 'Apellido Paterno.', '.manual-form');
        }

        if (!this.model.evaluatorDate) {
          add('evaluatorDate', 'Fecha de firma.', '.manual-form__date-field');
        }

        if (this.hasManualSignerInfo && !this.hasSignedDocument) {
          add(
            'signedDocumentId',
            'Documento firmado manuscrito.',
            '.document-row'
          );
        }

        return items;
      }

      if (!this.isElectronicSigned) {
        add(
          'electronicSignature',
          this.signatureHubInfo?.documentInfo
            ? 'Complete la firma electrónica en el Hub de Firmas.'
            : 'Configure y envíe el documento al Hub de Firmas.',
          '.signature-panel'
        );
      }

      return items;
    },
    async handleSelectMechanism(selected) {
      const value = this.getSelectedMechanismValue(selected);
      if (value !== true && value !== false) return;

      const nextSignatureMechanismId = this.toSignatureMechanismId(value);
      const previousValue = this.selectedMechanismSignPlan;
      const previousModel = this.getModelWithMechanism(previousValue);

      if (value === previousValue) return;

      const shouldConfirm = this.hasStartedSignatureProcess(previousValue);
      if (shouldConfirm) {
        const confirmed = await this.confirmMechanismChange(value);
        if (!confirmed) {
          this.restoreSignatureMechanismSelection(previousValue, previousModel);
          return;
        }

        const cleared = await this.clearSignatureProgress(previousValue);
        if (!cleared) {
          this.restoreSignatureMechanismSelection(previousValue, previousModel);
          return;
        }
      }

      if (nextSignatureMechanismId === SignatureMechanisms.Manual) {
        this.manualSignatureCleared = false;
      }

      this.model = this.getModelForSignatureMechanism(nextSignatureMechanismId);

      try {
        await this.saveSignatureInfo(nextSignatureMechanismId);
        this.selectedMechanismSignPlan = value;
        if (nextSignatureMechanismId === SignatureMechanisms.Electronic) {
          await this.fetchSignatureHubInfo();
        }
      } catch {
        this.restoreSignatureMechanismSelection(previousValue, previousModel);
        this.ShowToast(
          '',
          'No fue posible guardar el mecanismo de firma.',
          'error'
        );
      }
    },
    async addManualSigner() {
      const valid = await this.$refs.form.validate();
      if (!valid || !this.hasManualSignerInfo) return;

      this.manualSignatureCleared = false;
      this.manualProfilePrefilled = false;
      await this.saveSignatureInfo();
      this.manualSignerAdded = true;
    },
    async clearManualSigner() {
      this.clearManualSignature();
      await this.saveSignatureInfo();
      this.$nextTick(() => this.$refs.form?.reset());
    },
    clearManualSignature() {
      this.model = {
        ...this.model,
        evaluatorName: null,
        evaluatorInitialName: null,
        evaluatorLastName: null,
        evaluatorSecondLastName: null,
        evaluatorEmail: null,
        evaluatorDate: null,
        signedDocumentId: null,
        signedDocumentName: null,
      };
      this.manualSignatureCleared = true;
      this.manualProfilePrefilled = false;
      this.manualSignerAdded = false;
      this.hasDownloadedTemplate = false;
      this.selectedSignedDocument = null;
    },
    clearStaleManualSignature() {
      if (!this.manualSignatureCleared) return;

      this.clearManualSignature();
    },
    async clearSignedDocument() {
      if (!this.canDeleteSignedDocument) return;

      this.model = {
        ...this.model,
        signedDocumentId: null,
        signedDocumentName: null,
      };
      this.selectedSignedDocument = null;
      await this.saveSignatureInfo();
    },
    getModelWithMechanism(value) {
      return {
        ...copy(this.model),
        signatureMechanismId:
          value === true
            ? SignatureMechanisms.Electronic
            : value === false
            ? SignatureMechanisms.Manual
            : null,
      };
    },
    getSelectedMechanismValue(selected) {
      if (selected === true || selected === false) return selected;

      const label = selected?.label?.toLowerCase();
      if (label?.includes('manuscrito')) return false;
      if (label?.includes('electr')) return true;

      return selected?.value;
    },
    toSignPlanValue(signatureMechanismId) {
      const value = Number(signatureMechanismId);

      if (value === SignatureMechanisms.Electronic) return true;
      if (value === SignatureMechanisms.Manual) return false;

      return null;
    },
    toSignatureMechanismId(value) {
      return value === true
        ? SignatureMechanisms.Electronic
        : SignatureMechanisms.Manual;
    },
    getModelForSignatureMechanism(signatureMechanismId) {
      const model = {
        ...copy(this.model),
        signatureMechanismId,
      };

      if (signatureMechanismId === SignatureMechanisms.Manual) {
        return model;
      }

      return {
        ...model,
        evaluatorName: null,
        evaluatorInitialName: null,
        evaluatorLastName: null,
        evaluatorSecondLastName: null,
        evaluatorEmail: null,
        evaluatorDate: null,
        signedDocumentId: null,
        signedDocumentName: null,
      };
    },
    restoreSignatureMechanismSelection(value, model) {
      this.model = model;
      this.selectedMechanismSignPlan = value;
      this.$nextTick(() => this.$refs.signPlanRef?.setSelectedQuestion(value));
    },
    async clearSignatureProgress(previousValue) {
      if (previousValue === false) {
        this.clearManualSignature();
        return true;
      }

      if (previousValue === true) {
        return await this.clearElectronicSignature();
      }

      return true;
    },
    hasStartedSignatureProcess(previousValue) {
      if (previousValue === false) {
        return this.hasStartedManualSignatureProcess();
      }

      if (previousValue === true) {
        return this.hasStartedElectronicSignatureProcess();
      }

      return false;
    },
    hasStartedManualSignatureProcess() {
      return (
        [
          this.model.evaluatorName,
          this.model.evaluatorInitialName,
          this.model.evaluatorLastName,
          this.model.evaluatorSecondLastName,
          this.model.evaluatorEmail,
          this.model.evaluatorDate,
          this.model.signedDocumentId,
          this.model.signedDocumentName,
          this.selectedSignedDocument,
        ].some(Boolean) ||
        this.manualSignerAdded ||
        this.hasDownloadedTemplate
      );
    },
    hasStartedElectronicSignatureProcess() {
      return Boolean(
        this.signatureHubInfo?.signatureHubId ||
          this.signatureHubInfo?.documentId ||
          this.signatureHubInfo?.documentInfo
      );
    },
    async clearElectronicSignature() {
      if (!this.id || Number(this.id) <= 0) {
        this.signatureHubInfo = null;
        return true;
      }

      const documentStatusId = this.signatureHubInfo?.documentInfo
        ?.documentStatusId;

      if (
        documentStatusId === digitalSignatureDocumentStatus.SignaturePending
      ) {
        this.ShowToast(
          '',
          'Debe cancelar el proceso de firma electrÃ³nica desde el Hub de Firmas antes de cambiar a firma manuscrita.',
          'error'
        );
        return false;
      }

      if (documentStatusId === digitalSignatureDocumentStatus.Signed) {
        this.ShowToast(
          '',
          'No puede cambiar el mecanismo de firma porque el documento ya fue firmado.',
          'error'
        );
        return false;
      }

      try {
        await removeSignatureHub(
          SignatureHubTypes.REFERRAL_VOCATIONAL_REHABILITATION,
          this.id
        );
      } catch {
        this.ShowToast(
          '',
          'No fue posible limpiar la configuración de firma electrónica.',
          'error'
        );
        return false;
      }

      this.signatureHubInfo = null;
      return true;
    },
    async confirmMechanismChange(value) {
      const nextMechanismName = value === true ? 'electrónica' : 'manuscrita';
      return await this.ShowCancelModal(
        'Confirmación',
        `La información registrada en la firma se eliminará de forma permanente si cambia a firma ${nextMechanismName}. ¿Desea continuar?`,
        'Sí',
        'No'
      );
    },
    async saveSignatureInfo(signatureMechanismId = null) {
      if (!this.id || Number(this.id) <= 0) return;

      const payload = {
        tab: 'Signature',
        ...this.buildSignaturePayload(signatureMechanismId),
        id: this.id && this.id > 0 ? Number(this.id) : null,
        statusId: Number(this.status?.id ?? Statuses.Draft),
        isSubmitted: false,
      };
      await save(objectToFormData(payload, { indices: true }));
      this.$emit('update:initialModel', this.initialModel);
    },
    buildSignaturePayload(signatureMechanismId = null) {
      const model = {
        ...copy(this.model),
        signatureMechanismId:
          signatureMechanismId ?? this.model.signatureMechanismId,
      };
      const isManual =
        Number(model.signatureMechanismId) === SignatureMechanisms.Manual;

      return {
        ...model,
        evaluatorName: isManual ? model.evaluatorName || '' : '',
        evaluatorInitialName: isManual ? model.evaluatorInitialName || '' : '',
        evaluatorLastName: isManual ? model.evaluatorLastName || '' : '',
        evaluatorSecondLastName: isManual
          ? model.evaluatorSecondLastName || ''
          : '',
        evaluatorEmail: isManual ? model.evaluatorEmail || '' : '',
        evaluatorDate: isManual ? model.evaluatorDate || '' : '',
        signedDocumentId: isManual ? model.signedDocumentId || '' : '',
        signedDocumentName: isManual ? model.signedDocumentName || '' : '',
      };
    },
    async downloadTemplate() {
      try {
        await this.saveSignatureInfo();
        const response = await getReport(this.id);
        peiGeneralService.downloadDocument(response.data);
        this.hasDownloadedTemplate = true;
      } catch {
        this.ShowToast('', 'No fue posible descargar el referido.', 'error');
      }
    },
    async saveSignedDocumentFile(file) {
      if (this.isReadonlyMode) return;
      if (!file || !this.id || Number(this.id) <= 0) return;

      try {
        const { data } = await saveSignedDocument(
          this.id,
          objectToFormData({ file }, { indices: true })
        );
        this.model = {
          ...this.model,
          signedDocumentId: data.documentId,
          signedDocumentName: data.fileName,
        };
        await this.saveSignatureInfo();
        this.$emit('clear-validation');
        this.ShowToast('', 'Documento guardado exitosamente.', 'success');
      } catch {
        this.ShowToast(
          '',
          'No fue posible guardar el documento firmado.',
          'error'
        );
      }
    },
    async downloadSignedDocument() {
      if (!this.model.signedDocumentId) return;
      await documentsService.downloadFromServer(this.model.signedDocumentId);
    },
  },
};
</script>

<style lang="scss" scoped>
.signature-tab {
  padding: 18px 8px 8px;
}

.signature-warning {
  background: #fff3df;
  border: 1px solid #df9b3a;
  border-radius: 4px;
  color: #b26400;
  font-weight: 600;
  padding: 10px 14px;
}

.tab-validation-warning {
  background: #fff7e8;
  border: 1px solid #d99025;
  border-radius: 4px;
  color: #8a5200;
  padding: 10px 12px;
}

.tab-validation-warning__message {
  align-items: center;
  display: flex;
  font-weight: 700;
  margin-bottom: 6px;
}

.tab-validation-warning__items {
  margin: 0;
  padding-left: 26px;
}

.tab-validation-warning__items li {
  margin: 2px 0;
}

.signature-panel {
  margin-top: 18px;
  padding-top: 18px;
}

.signature-placeholder,
.document-row,
.manual-summary {
  background: #f8f9f8;
  border-radius: 4px;
  padding: 16px;
}

.manual-form {
  align-items: start;
  background: #f8f9f8;
  display: grid;
  gap: 14px;
  grid-template-columns:
    minmax(170px, 1fr) 80px minmax(170px, 1fr) minmax(170px, 1fr)
    minmax(190px, 1fr) auto;
  padding: 16px;
}

.manual-form > span {
  min-width: 0;
}

.manual-form label,
.manual-summary span,
.document-row span {
  color: #686f70;
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
}

.manual-form__actions {
  align-self: end;
}

.manual-summary {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(6, minmax(120px, 1fr)) auto;
}

.manual-summary__clear {
  background: transparent;
  border: 0;
  color: #ff5b5c;
  font-size: 16px;
}

.document-row {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(240px, 0.8fr) minmax(280px, 1fr) auto;
}

.document-row--center {
  display: flex;
  justify-content: center;
}

.document-row__actions {
  display: flex;
  justify-content: flex-end;
}

::v-deep .input-label,
::v-deep label {
  color: #686f70;
  font-size: 13px;
  font-weight: 500;
}

.manual-form__date-field ::v-deep div > label {
  margin-bottom: 0 !important;
}

::v-deep .data-label {
  font-size: 13px !important;
}
@media (max-width: 1199.98px) {
  .manual-form,
  .manual-summary,
  .document-row {
    grid-template-columns: 1fr;
  }
}
</style>
